import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
} from '@mui/joy';
import Grid from '@mui/joy/Grid';
import {
  CreditCard,
  DownloadSimple as Download,
  Receipt,
  Users as People,
  Tag as LocalOffer,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/common/PageHeader';
import { WalletCard } from '../../components/settings/WalletCard';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Per-seat billing subscription interface
 */
interface Subscription {
  id: number;
  // Per-seat billing fields
  seat_count: number;
  price_per_seat: string;
  is_free: boolean;
  seat_discount: string;
  effective_price_per_seat: string;
  total_monthly_cost: string;
  // Status
  status: string;
  status_display: string;
  billing_cycle: 'monthly' | 'annual';
  billing_cycle_display: string;
  // Stripe
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  // Dates
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string;
  current_period_end: string;
  canceled_at: string | null;
  cancel_at_period_end: boolean;
  cancel_at: string | null;
  cancellation_effective_at: string | null;
  pause_collection: { behavior?: string; resumes_at?: number };
  // Computed
  is_paid_plan: boolean;
  can_manage_billing: boolean;
  // Legacy (deprecated)
  tier_details?: {
    name: string;
    tier_code: string;
  };
}

interface PaymentHistory {
  id: string;
  invoice_number: string;
  amount: number;
  currency: string;
  status: string;
  paid: boolean;
  date: string;
  period_start: string | null;
  period_end: string | null;
  description: string;
  invoice_pdf: string;
  payment_method: string;
}

interface PaymentMethod {
  id: string;
  type: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  } | null;
  billing_details: {
    name: string | null;
  };
}

interface PaymentMethodResponse {
  payment_method: PaymentMethod | null;
}

export const BillingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const canManage = Boolean(user?.is_organization_admin);

  // Refresh on every visit, return from Stripe, and window focus. Dashboard
  // cancellations do not redirect through ApexOS, and webhooks can be delayed.
  React.useEffect(() => {
    if (searchParams.get('stripe_return') !== '1') return;
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
    queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
    queryClient.invalidateQueries({ queryKey: ['payment-method'] });
    queryClient.invalidateQueries({ queryKey: ['payment-history'] });
    navigate('/settings/billing', { replace: true });
  }, [searchParams, queryClient, navigate]);

  // Fetch subscription data
  const { data: billing, isLoading: loadingSubscription, refetch, isFetching, isError } = useQuery({
    queryKey: ['subscription', user?.organization?.id],
    enabled: canManage,
    queryFn: async () => {
      let syncFailed = false;
      try {
        await api.post('/api/subscriptions/sync_from_stripe/', {});
        queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      } catch {
        syncFailed = true;
      }
      const response = await api.get('/api/subscriptions/');
      return { subscriptions: response.data, syncFailed };
    },
    refetchInterval: 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
  const subscriptions = billing?.subscriptions;

  // Parse subscription from response
  const subscription: Subscription | null = React.useMemo(() => {
    if (!subscriptions) return null;

    // If it's a paginated response
    if (typeof subscriptions === 'object' && subscriptions !== null && 'results' in subscriptions) {
      const results = (subscriptions as { results: Subscription[] }).results;
      if (Array.isArray(results)) {
        return results[0] || null;
      }
    }

    // If it's an array
    if (Array.isArray(subscriptions)) {
      return subscriptions[0] || null;
    }

    // If it's a direct subscription object
    if (typeof subscriptions === 'object' && subscriptions !== null && 'id' in subscriptions) {
      return subscriptions as unknown as Subscription;
    }

    return null;
  }, [subscriptions]);

  // Fetch payment history
  const { data: paymentHistory, isLoading: loadingPayments } = useQuery({
    queryKey: ['payment-history', user?.organization?.id],
    queryFn: () => api.get('/api/subscriptions/payment_history/').then(res => res.data as PaymentHistory[]),
    enabled: canManage && !!subscription,
  });

  // Fetch payment method
  const { data: paymentMethodData } = useQuery({
    queryKey: ['payment-method', user?.organization?.id],
    queryFn: () => api.get('/api/subscriptions/payment-method/').then(res => res.data as PaymentMethodResponse),
    // Free orgs included: they can save a card for the usage-based products
    // (texts and calls, direct mail), and it has to render once they have.
    enabled: canManage && !!subscription,
  });

  // Manage billing portal
  const { mutate: openBillingPortal, isPending: openingPortal } = useMutation({
    mutationFn: () => {
      // Always include ?stripe_return=1 in the return URL so the page-mount
      // effect above kicks off the defensive sync when Stripe redirects us
      // back. This works whether the user is going to the Billing Portal
      // (existing customer) or the Checkout flow (trial user adding first
      // card — see create_billing_portal_session backend fallback).
      const returnUrl = new URL('/settings/billing', window.location.origin);
      returnUrl.searchParams.set('stripe_return', '1');
      return api.post('/api/subscriptions/create_billing_portal_session/', {
        return_url: returnUrl.toString(),
      });
    },
    onSuccess: (data) => {
      const portalData = data.data as { portal_url: string };
      window.location.href = portalData.portal_url;
    },
  });

  // Saving a first card on the free plan. Deliberately NOT the billing portal
  // endpoint: with no Stripe customer that one falls back to per-seat
  // Checkout, so a free org would come back on a paid subscription. This runs
  // Checkout in setup mode — a card is saved, nothing is started.
  const { mutate: addFreePlanCard, isPending: addingCard } = useMutation({
    mutationFn: () => {
      const returnUrl = new URL('/settings/billing', window.location.origin);
      returnUrl.searchParams.set('stripe_return', '1');
      return api.post('/api/subscriptions/create-setup-session/', {
        return_url: returnUrl.toString(),
      });
    },
    onSuccess: (data) => {
      const setupData = data.data as { portal_url: string };
      window.location.href = setupData.portal_url;
    },
  });

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numAmount);
  };

  // Get status color
  const getStatusColor = (status: string): 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'success';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'unpaid':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  // Hiding the tab isn't enough — the route is still reachable by URL, and the
  // page would otherwise fire admin-only requests and render a wall of errors.
  if (user && !user.is_organization_admin) {
    return (
      <Box sx={{ p: 3, maxWidth: 640, mx: 'auto' }}>
        <Alert color="neutral" variant="soft">
          Billing is managed by your organization's admins. Ask one of them to
          change the plan, update the card on file, or add funds.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (loadingSubscription) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) return <Alert color="danger" endDecorator={<Button onClick={() => refetch()}>Retry</Button>}>Billing details could not be loaded.</Alert>;

  // No subscription
  if (!subscription) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">
          No subscription found. Please contact support.
        </Alert>
      </Box>
    );
  }

  const cancellationDate = subscription.cancellation_effective_at || subscription.cancel_at || (
    subscription.cancel_at_period_end ? subscription.current_period_end : null
  );
  const terminal = ['canceled', 'incomplete_expired'].includes(subscription.status);
  const ending = !subscription.is_free && !terminal && Boolean(cancellationDate);
  const endsThisPeriod = ending && new Date(cancellationDate!).getTime() <= new Date(subscription.current_period_end).getTime();
  const collectionPaused = !terminal && Boolean(subscription.pause_collection?.behavior);
  const statusLabel = subscription.is_free ? 'Free' : ending
    ? `${subscription.status === 'active' ? 'Active until' : `${subscription.status_display}; cancels`} ${formatDate(cancellationDate!)}`
    : collectionPaused ? 'Collection paused' : subscription.status_display;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <PageHeader title="Billing & subscription" description="Manage your plan and payment method. Your usage balance covers phone, text, and carrier fees separately." />

      {billing?.syncFailed && <Alert color="warning" sx={{ mb: 2 }} endDecorator={<Button size="sm" loading={isFetching} onClick={() => refetch()}>Retry sync</Button>}>
        Stripe could not be refreshed. These are the last saved billing details.
      </Alert>}

      {/* Usage balance sits above the plan: when texting stops, this is the
          answer, and the plan below is almost never the problem. */}
      <WalletCard canManage={Boolean(user?.is_organization_admin)} />

      <Grid container spacing={3}>
        {/* Current Plan Card */}
        <Grid xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={1.5} sx={{ mb: 2 }}>
                <Box>
                  <Typography level="h3" sx={{ mb: 1 }}>
                    {subscription.is_free ? 'Free Plan' : 'ApexOS Plan'}
                  </Typography>
                  <Chip
                    color={subscription.is_free ? 'success' : ending || collectionPaused ? 'warning' : getStatusColor(subscription.status)}
                    variant="soft"
                    size="sm"
                  >
                    {statusLabel}
                  </Chip>
                </Box>
                {!subscription.is_free && subscription.can_manage_billing && (
                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<CreditCard />}
                    onClick={() => openBillingPortal()}
                    loading={openingPortal}
                  >
                    Manage Billing
                  </Button>
                )}
              </Stack>

              {ending && <Alert color="warning" sx={{ mb: 2 }}>Cancellation scheduled. Your plan ends on {formatDate(cancellationDate!)} and will not renew after that date.</Alert>}
              {!subscription.is_free && collectionPaused && <Alert color="neutral" sx={{ mb: 2 }}>Stripe subscription collection is paused.</Alert>}

              {subscription.is_free ? (
                /* Free Plan Display */
                <Alert color="success" sx={{ mb: 2 }}>
                  Your organization has complimentary access to ApexOS. All features are included.
                </Alert>
              ) : (
                /* Per-Seat Billing Display */
                <>
                  <Box sx={{ mb: 3 }}>
                    <Typography level="h1" sx={{ color: 'var(--brand-text, #7013f5)' }}>
                      {formatCurrency(subscription.total_monthly_cost)}
                      <Typography level="body-sm" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                        /month
                      </Typography>
                    </Typography>

                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                      <People color="var(--joy-palette-text-secondary)" />
                      <Typography level="body-md">
                        {subscription.seat_count} user{subscription.seat_count !== 1 ? 's' : ''} × {formatCurrency(subscription.effective_price_per_seat)}/user
                      </Typography>
                    </Stack>

                    {parseFloat(subscription.seat_discount) > 0 && (
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                        <LocalOffer color="var(--joy-palette-success-500)" />
                        <Typography level="body-sm" sx={{ color: 'success.600' }}>
                          Discount: {formatCurrency(subscription.seat_discount)}/user off (base price: {formatCurrency(subscription.price_per_seat)}/user)
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Billing Period Info */}
                  <Grid container spacing={2}>
                    <Grid xs={6} sm={3}>
                      <Typography level="body-xs" textColor="text.tertiary">
                        Billing Cycle
                      </Typography>
                      <Typography level="body-md">
                        {subscription.billing_cycle_display}
                      </Typography>
                    </Grid>
                    <Grid xs={6} sm={3}>
                      <Typography level="body-xs" textColor="text.tertiary">
                        Current Period
                      </Typography>
                      <Typography level="body-md">
                        {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                      </Typography>
                    </Grid>
                    {subscription.trial_end && subscription.status === 'trialing' && (
                      <Grid xs={6} sm={3}>
                        <Typography level="body-xs" textColor="text.tertiary">
                          Trial Ends
                        </Typography>
                        <Typography level="body-md" sx={{ color: 'warning.500' }}>
                          {formatDate(subscription.trial_end)}
                        </Typography>
                      </Grid>
                    )}
                    {ending && (
                      <Grid xs={6} sm={3}>
                        <Typography level="body-xs" textColor="text.tertiary">
                          Cancels On
                        </Typography>
                        <Typography level="body-md" sx={{ color: 'danger.500' }}>
                          {formatDate(cancellationDate!)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Method */}
        <Grid xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography level="h4" sx={{ mb: 2 }}>
                Payment Method
              </Typography>

              {paymentMethodData?.payment_method?.card ? (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <CreditCard size={32} color="var(--joy-palette-primary-500)" />
                    <Box>
                      <Typography level="title-md">
                        {paymentMethodData.payment_method.card.brand.toUpperCase()} •••• {paymentMethodData.payment_method.card.last4}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                        Expires {paymentMethodData.payment_method.card.exp_month.toString().padStart(2, '0')}/{paymentMethodData.payment_method.card.exp_year}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  {subscription.is_free ? (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                      Charged only when you buy texts, calls, or direct mail. Your plan stays free.
                    </Typography>
                  ) : endsThisPeriod ? (
                    <Typography level="body-sm" sx={{ mb: 2 }}>Plan ends: <strong>{formatDate(cancellationDate!)}</strong>. No plan renewal is scheduled.</Typography>
                  ) : collectionPaused ? (
                    <Typography level="body-sm" sx={{ mb: 2 }}>Subscription collection is paused{subscription.pause_collection.resumes_at ? ` until ${formatDate(new Date(subscription.pause_collection.resumes_at * 1000).toISOString())}` : ''}.</Typography>
                  ) : ['canceled', 'incomplete_expired', 'paused'].includes(subscription.status) ? (
                    <Typography level="body-sm" sx={{ mb: 2 }}>No plan renewal payment is scheduled.</Typography>
                  ) : (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                      Next billing date: <strong>{formatDate(subscription.current_period_end)}</strong>
                    </Typography>
                  )}

                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    fullWidth
                    onClick={() => openBillingPortal()}
                    loading={openingPortal}
                  >
                    Update Payment Method
                  </Button>
                </Box>
              ) : (
                <Box>
                  {/* On the free plan a missing card is not a problem to warn
                      about — the plan needs none. It only gates the things you
                      buy separately, so say what adding one unlocks instead. */}
                  {subscription.is_free ? (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                      Optional. Add a card to send texts and calls or mail a direct mail campaign —
                      you are charged only for what you buy, and your plan stays free.
                    </Typography>
                  ) : (
                    <>
                      <Alert color="warning" sx={{ mb: 2 }}>
                        No payment method on file
                      </Alert>

                      {subscription.status === 'trialing' && (
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                          Trial ends: <strong>{subscription.trial_end ? formatDate(subscription.trial_end) : 'N/A'}</strong>
                        </Typography>
                      )}
                    </>
                  )}

                  <Button
                    variant="solid"
                    color="primary"
                    size="sm"
                    fullWidth
                    startDecorator={<CreditCard />}
                    onClick={() => (subscription.is_free ? addFreePlanCard() : openBillingPortal())}
                    loading={subscription.is_free ? addingCard : openingPortal}
                  >
                    Add Payment Method
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Payment History */}
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography level="h4" startDecorator={<Receipt />}>
                  Payment History
                </Typography>
              </Stack>

              {loadingPayments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size="sm" />
                </Box>
              ) : !paymentHistory || paymentHistory.length === 0 ? (
                <Typography level="body-sm" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                  No payment history yet.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 600 }}>
                  <thead>
                    <tr>
                      <th style={{ width: '25%' }}>Date</th>
                      <th style={{ width: '25%' }}>Invoice</th>
                      <th style={{ width: '20%' }}>Amount</th>
                      <th style={{ width: '15%' }}>Status</th>
                      <th style={{ width: '15%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment) => (
                      <tr key={payment.id}>
                        <td>{formatDate(payment.date)}</td>
                        <td>{payment.invoice_number || '-'}</td>
                        <td>{formatCurrency(payment.amount)}</td>
                        <td>
                          <Chip
                            size="sm"
                            color={payment.paid ? 'success' : 'warning'}
                            variant="soft"
                          >
                            {payment.paid ? 'Paid' : payment.status}
                          </Chip>
                        </td>
                        <td>
                          {payment.invoice_pdf && (
                            <Button
                              size="sm"
                              variant="plain"
                              color="neutral"
                              startDecorator={<Download />}
                              component="a"
                              href={payment.invoice_pdf}
                              target="_blank"
                            >
                              PDF
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingPage;
